/**
 * Raised when a learner signs in successfully, but against a different tenant than the portal URL
 * they are on.
 *
 * The tenant is the first path segment of the portal URL, so a learner who follows a link for one
 * organization while their account belongs to another authenticates fine and then fails a check
 * they cannot act on. The access token carries the tenant their account actually belongs to, which
 * is enough to tell them where to go instead.
 */
export class WrongTenantError extends Error {
  /** The tenant the account belongs to, when the token carried one. */
  readonly tenantCode?: string;

  /** The portal URL for that tenant, when it could be derived. */
  readonly signInUrl?: string;

  constructor(tenantCode?: string, origin: string = window.location.origin) {
    super(buildMessage(tenantCode, origin));

    // Keeps `instanceof` working once TypeScript downlevels to ES5, where extending a built-in
    // otherwise loses the prototype.
    Object.setPrototypeOf(this, WrongTenantError.prototype);

    this.name = 'WrongTenantError';
    this.tenantCode = tenantCode;
    this.signInUrl = tenantCode ? buildSignInUrl(tenantCode, origin) : undefined;
  }
}

function buildSignInUrl(tenantCode: string, origin: string): string {
  return `${origin}/${encodeURIComponent(tenantCode)}`;
}

function buildMessage(tenantCode: string | undefined, origin: string): string {
  if (!tenantCode) {
    // Nothing useful to point them at, so do not guess a URL - say what is wrong and who can fix it.
    return "This account isn't registered for this organization's portal. Check the link in your invitation email, or contact your administrator.";
  }

  return `This account belongs to a different organization. Sign in at ${buildSignInUrl(tenantCode, origin)} instead.`;
}
